import React from 'react';
import { motion } from 'framer-motion';

const AboutUs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      style={styles.container}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div style={styles.infoContainer} variants={itemVariants}>
        <motion.h2 style={styles.heading} variants={itemVariants}>About Us</motion.h2>
        <motion.h3 style={styles.subHeading} variants={itemVariants}>PREDICT. PLAN. PROSPER - SMARTER WOMEN'S HEALTH 🌿</motion.h3>
        <motion.p style={styles.info} variants={itemVariants}>
          Welcome to <strong>UmbraCare</strong>, your all-in-one Maternal and Menstrual Health Companion. Whether you're tracking your cycle, navigating pregnancy, embracing motherhood, or managing menopause, we're here to support, inform, and empower you every step of the way.
        </motion.p>
        <motion.p style={styles.info} variants={itemVariants}>
          Our mission is to provide a Safe, Inclusive, and Scientifically-backed Platform that prioritizes women's health and well-being. UmbraCare covers a wide range of topics, including Menstruation, Pregnancy, Postpartum Recovery, Fertility, Menopause, and overall Reproductive Health.
        </motion.p>
        <motion.p style={styles.info} variants={itemVariants}>
          We, at UmbraCare, understand that every woman's journey is unique, which is why our platform offers personalized Tracking, Expert Insights, and a Community-driven Space to help you make informed decisions about your health, driven by women themselves!
        </motion.p>

        <motion.h3 style={styles.subHeading} variants={itemVariants}>✨ What UmbraCare Offers</motion.h3>

        <motion.div style={styles.featureSection} variants={itemVariants}>
          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Pregnancy & Postpartum Tracker</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Monitor fetal development with weekly updates.</motion.li>
            <motion.li variants={itemVariants}>Receive personalized postpartum recovery tips and reminders.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Menopause Insights</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Track perimenopause and menopause symptoms over time.</motion.li>
            <motion.li variants={itemVariants}>Get expert-backed advice on hormone balance and lifestyle changes.</motion.li>
            <motion.li variants={itemVariants}>Learn about nutrition and exercises that support this phase.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Period & Menstrual Health Tracker</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Predict your next period and track cycle irregularities.</motion.li>
            <motion.li variants={itemVariants}>Get timely reminders to stay prepared for your cycle.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>IVF & Fertility Tracker</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Track your ovulation and fertility window with precision.</motion.li>
            <motion.li variants={itemVariants}>Log your IVF cycle progress and check according to your embryo transfer.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Smart Notifications & Reminders</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Receive personalized alerts for upcoming periods, ovulation, fertility windows, and doctor visits.</motion.li>
            <motion.li variants={itemVariants}>Stay updated on hydration, nutrition, and self-care tips.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>AI-Powered Chatbot</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>A compassionate, 24/7 assistant to answer your health-related queries.</motion.li>
            <motion.li variants={itemVariants}>Covers menstruation, pregnancy, postpartum care, fertility, and menopause.</motion.li>
            <motion.li variants={itemVariants}>Has voice search feature for easy implementation and supports Multi-Language effectively for user's ease.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Weekly Wellness Newsletter</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>Stay informed with expert advice on reproductive health, self-care, fitness, and mental well-being.</motion.li>
            <motion.li variants={itemVariants}>Get personalized insights and updates delivered straight to your dashboard.</motion.li>
          </motion.ul>

          <motion.h4 style={styles.featureTitle} variants={itemVariants}>Doctor's Information (Coming Soon! 🚀)</motion.h4>
          <motion.ul style={styles.featureList} variants={itemVariants}>
            <motion.li variants={itemVariants}>We're working on a feature that will allow you to connect with trusted gynecologists and healthcare professionals for expert consultations and appointment booking.</motion.li>
          </motion.ul>
        </motion.div>

        <motion.p style={styles.info} variants={itemVariants}>
          At UmbraCare, we believe that your health journey deserves care, precision, and compassion. From the first period to motherhood and beyond, we're here to make every phase smoother, healthier, and more empowering.
        </motion.p>
        <motion.p style={styles.tagline} variants={itemVariants}>
          🌸 Your Health. Your Journey. Your UmbraCare.
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontFamily: "'Poppins', sans-serif",
    padding: '20px',
    paddingTop: '90px',
    backgroundImage: 'url(/background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    zIndex: 1,
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(253, 232, 233, 0.7)',
      zIndex: -1,
    }
  },
  infoContainer: {
    width: '100%',
    maxWidth: '1000px',
    padding: '40px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(5px)',
    borderRadius: '10px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    zIndex: 2,
    border: '2px solid #B85170',
    textAlign: 'center',
  },
  heading: {
    fontSize: '28px',
    color: '#B85170',
    marginBottom: '20px',
    fontWeight: '600',
  },
  subHeading: {
    fontSize: '20px',
    color: '#B85170',
    marginBottom: '15px',
    fontWeight: '500',
  },
  info: {
    fontSize: '16px',
    color: '#000000',
    marginBottom: '15px',
    lineHeight: '1.5',
  },
  tagline: {
    fontSize: '18px',
    color: '#B85170',
    fontWeight: '500',
    marginTop: '20px',
  },
  featureSection: {
    textAlign: 'left',
    marginBottom: '20px',
  },
  featureTitle: {
    fontSize: '18px',
    color: '#B85170',
    marginBottom: '10px',
    fontWeight: '500',
  },
  featureList: {
    listStyleType: 'disc',
    paddingLeft: '20px',
    marginBottom: '20px',
    color: '#000000',
  },
};

export default AboutUs;